import React, { useState } from 'react';

import ModalContainer from '@components/modals/ModalContainer';
import CommonImage from '@components/CommonImage';
import ModalHeader from '@components/modals/ModalHeader';
import CustomDropdown from '@components/CustomDropdown';

import useSingleFileUpload from '@hooks/useSingleFileUpload';

import {
	BUYER_ETD_UPDATE_REASON,
	etdUpdateRequestedBy,
	FACTORY_ETD_UPDATE_REASON,
	NITEX_ETD_UPDATE_REASON
} from '@features/order/utils/projectUtil';

import { adjustTextAreaHeight, checkAnyUserType } from '@utils/Util';

const EtdUpdateLog = ({ onClose, onSubmit }) => {
	const isSealsTeam = () =>
		checkAnyUserType(['ACCOUNT_MANAGER', 'BUSINESS_DEVELOPMENT_MANAGER']) &&
		!checkAnyUserType(['PROJECT_MANAGER']);
	const requestedByOptions = etdUpdateRequestedBy.filter((item) => {
		if (isSealsTeam()) return item.value === 'BUYER';
		return true;
	});

	const [requestedBy, setRequestedBy] = useState(() => requestedByOptions[0]?.value);
	const [selectedReason, setSelectedReason] = useState({});
	const [uploadedFile, setUploadedFile] = useState(null);
	const [remarks, setRemarks] = useState('');
	const [errors, setErrors] = useState({});
	const { readFile } = useSingleFileUpload();

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (file) {
			const fileStr = await readFile(file);
			const imageData = {
				name: file.name,
				base64Str: fileStr,
				docMimeType: file.type,
				print: true,
				documentType: 'OTHER'
			};
			setUploadedFile(imageData);
		}
	};

	const renderDropdownItems = () => {
		if (requestedBy === 'BUYER') return BUYER_ETD_UPDATE_REASON;
		else if (requestedBy === 'NITEX') return NITEX_ETD_UPDATE_REASON;
		else return FACTORY_ETD_UPDATE_REASON;
	};

	const isValid = () => {
		let errorObj = {};
		if (!selectedReason?.value) {
			errorObj['selectedReason'] = 'Please select a reason';
		}
		if (requestedBy !== 'NITEX' && !uploadedFile) {
			errorObj['uploadedFile'] = 'Please upload the proof';
		}
		if (selectedReason.value === 'Others' && !remarks) {
			errorObj['remarks'] = 'Please enter your message';
		}
		setErrors(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const handleSubmit = () => {
		if (isValid()) {
			let postData = {
				deliveryDateChangeRequestorType: requestedBy,
				deliveryDateChangeReason: selectedReason.value === 'Others' ? remarks : selectedReason.value,
				deliveryDateChangeProof: uploadedFile
			};
			onSubmit(postData);
		}
	};

	return (
		<ModalContainer className="update-buyer-etd-modal">
			<div className="popup-wrapper">
				<div className="common-popup material-request-details-modal">
					<ModalHeader title="Updating Buyer ETD" onClose={onClose} />
					<div className="common-popup-body">
						<div className="choose-puprpose-box">
							<p className="regular-14 gray_dark_01 mb-2">Requested by*</p>
							<div className="row">
								<div className="col-md-12">
									<div className="d-flex justify-content-start gap-3 mb-12">
										{requestedByOptions?.map((item) => (
											<div className="custom-radio-box custom-radio">
												<div className="form-group d-flex align-items-center">
													<input
														type="radio"
														id={item.value}
														name="purpose"
														value={item.value}
														checked={requestedBy === item.value}
														onChange={() => {
															setRequestedBy(item.value);
															setSelectedReason({});
															setUploadedFile(null);
															setRemarks('');
														}}
													/>
													<label
														htmlFor={item.value}
														className="ml-2 mb-0 gray_dark_01 regular-14"
													>
														{item.label}
													</label>
												</div>
											</div>
										))}
									</div>
								</div>
								<div className="col-md-12 mb-3">
									<label htmlFor="collection">Reason*</label>
									<CustomDropdown
										type="addItem"
										title="Reason"
										onItemClick={(value) => {
											setSelectedReason(value);
										}}
										items={renderDropdownItems()}
										selectedItem={selectedReason?.label}
									/>
									{errors.selectedReason && (
										<p className="error">{errors.selectedReason}</p>
									)}
								</div>
								{selectedReason?.value === 'Others' && (
									<div className="col-md-12">
										<div className="form-group mb-12">
											<label htmlFor="message">Remarks*</label>
											<textarea
												value={remarks}
												onChange={(e) => setRemarks(e.target.value)}
												rows="3"
												placeholder="Enter your message"
												onInput={(e) => adjustTextAreaHeight(e, '84px')}
											/>
											{errors.remarks && <p className="error">{errors.remarks}</p>}
										</div>
									</div>
								)}
								{requestedBy !== 'NITEX' && (
									<div className="col-md-12">
										<div className="form-group mb-12">
											<label htmlFor="multipleUpload">Attachments*</label>
											<div className="inline-upload">
												<label
													htmlFor="multipleUpload"
													className="regular-14 border-gray_dark_01"
												>
													<CommonImage src="/icons/upload-dark.svg" /> Upload
												</label>
												<input
													type="file"
													className="file-upload"
													id="multipleUpload"
													name="file"
													accept=".jpg,.jpeg,.png,application/pdf"
													onChange={handleFileChange}
												/>
												{errors.uploadedFile && (
													<p className="error">{errors.uploadedFile}</p>
												)}
											</div>
										</div>
										<div className="all-pdf-files d-flex flex-wrap justify-content-start align-items-center mb-12">
											{uploadedFile && (
												<div className="single-pdf-file d-flex align-items-center mr-4">
													<span className="regular-14 ml-1">
														{uploadedFile.name}
													</span>
													<div
														className="acton-img cursor-pointer ml-1"
														onClick={() => setUploadedFile(null)}
													>
														<CommonImage src="/icons/delete_red.svg" width={16} />
													</div>
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="modal-footer">
						<div className="footer-buttons text-right">
							<button className="button outline size32 ml-2" onClick={onClose}>
								Cancel
							</button>
							<button className="button size32 ml-2" onClick={handleSubmit}>
								Submit
							</button>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default EtdUpdateLog;
